import type { ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { tokens } from "../../theme";

interface Props {
  /** Uppercase eyebrow in the tinted strip, e.g. "GLOBAL APPS". */
  eyebrow: string;
  /** Right-hand side of the strip - a count, a link, a button. */
  action?: ReactNode;
  /** One line under the eyebrow explaining what the card governs. */
  hint?: string;
  children: ReactNode;
  "data-testid"?: string;
}

/**
 * The card shell every Component pane shares: `surface` fill, 14px radius,
 * `shadowCard`, and a `rowAlt` strip carrying an eyebrow plus one action.
 *
 * Extracted from the Information directory's section cards, which were the only
 * pane that looked like this - the rest were bare bordered lists, so moving
 * between Component tabs felt like moving between two different products.
 */
export default function EditorCard({
  eyebrow,
  action,
  hint,
  children,
  "data-testid": testId,
}: Props) {
  return (
    <Box
      data-testid={testId}
      sx={{
        bgcolor: tokens.surface,
        border: "1px solid",
        borderColor: tokens.divider,
        borderRadius: "14px",
        boxShadow: tokens.shadowCard,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          px: 2.25,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: tokens.divider,
          bgcolor: tokens.rowAlt,
        }}
      >
        <Typography
          variant="micro"
          sx={{ typography: "eyebrow", color: tokens.inkFaint }}
        >
          {eyebrow}
        </Typography>
        {action}
      </Stack>
      <Box sx={{ p: 2.25 }}>
        {hint && (
          <Typography
            variant="meta"
            sx={{ lineHeight: 1.5, color: tokens.inkMuted, mb: 2 }}
          >
            {hint}
          </Typography>
        )}
        {children}
      </Box>
    </Box>
  );
}
